import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle, AlertTriangle, HelpCircle, X, Copy, Share2, Eye, Scale
} from 'lucide-react';
import ChallengeForm from './ChallengeForm';
import { useLanguage } from '../context/LanguageContext';

export default function AnalysisResult({ result, onClose, onShare, onChallenge }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [showChallenge, setShowChallenge] = useState(false);

  const verdictConfig = {
    probably_authentic: { icon: CheckCircle, label: t('result_authentic'), color: 'text-emerald-400', bg: 'verdict-authentic' },
    probably_fake: { icon: AlertTriangle, label: t('result_fake'), color: 'text-rose-400', bg: 'verdict-fake' },
    uncertain: { icon: HelpCircle, label: t('result_uncertain'), color: 'text-amber-400', bg: 'verdict-uncertain' }
  };

  const config = verdictConfig[result.verdict] || verdictConfig.uncertain;
  const VerdictIcon = config.icon;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result.shareable_summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="w-full max-w-3xl mx-auto mt-8"
    >
      <div className={`glass rounded-2xl p-6 border ${config.bg}`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl bg-white/5 ${config.color}`}>
              <VerdictIcon className="w-8 h-8" />
            </div>
            <div>
              <h3 className={`text-xl font-bold ${config.color}`}>{config.label}</h3>
              <p className="text-slate-400 text-sm">{t('result_done')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-white/5">
            <div className="flex items-center gap-4">
              <div
                className="relative w-20 h-20 rounded-full flex items-center justify-center"
                style={{
                  background: `conic-gradient(from 180deg, ${result.confidence_score >= 70 ? '#10B981' : result.confidence_score >= 40 ? '#FBBF24' : '#F43F5E'} ${result.confidence_score}%, rgba(255,255,255,0.1) ${result.confidence_score}%)`
                }}
              >
                <div className="absolute inset-2 rounded-full bg-slate-900 flex items-center justify-center">
                  <span className="text-xl font-bold text-white">{result.confidence_score}%</span>
                </div>
              </div>
              <div>
                <p className="text-slate-300 text-sm mb-1">{t('result_confidence')}</p>
                <p className="text-white font-medium">
                  {result.confidence_score >= 70 ? t('result_high') : result.confidence_score >= 40 ? t('result_medium') : t('result_low')}
                </p>
              </div>
            </div>
          </div>

          {result.metadata && result.metadata.llms_used && (
            <div className="p-4 rounded-xl bg-white/5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-slate-400 text-sm">{t('result_llms_used')}</p>
                {result.metadata.analysis_grade && (
                  <span className="px-2 py-1 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-amber-300 text-xs font-semibold">
                    {result.metadata.analysis_grade_icon} {result.metadata.analysis_grade}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {result.metadata.llms_used.map((llm, idx) => (
                  <span key={idx} className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 text-xs font-medium">
                    {llm}
                  </span>
                ))}
              </div>
              {result.metadata.consensus !== undefined && (
                <p className="text-xs mt-2">
                  <span className={result.metadata.consensus ? 'text-emerald-400' : 'text-amber-400'}>
                    {result.metadata.consensus ? t('result_consensus') : t('result_divergence')}
                  </span>
                </p>
              )}
            </div>
          )}
        </div>

        {result.signals && result.signals.length > 0 && (
          <div className="mb-6">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4 text-emerald-400" />
              {t('result_signals')}
            </h4>
            <div className="space-y-2">
              {result.signals.map((signal, idx) => {
                const signalMeta = {
                  unverified_source: { icon: '\u26a0\ufe0f', label: 'Source non v\u00e9rifi\u00e9e', color: 'amber' },
                  rumor: { icon: '\ud83d\udcac', label: 'Rumeur', color: 'amber' },
                  engagement_bait: { icon: '\ud83c\udfa3', label: 'App\u00e2t \u00e0 engagement', color: 'rose' },
                  fictional_event: { icon: '\ud83c\udfac', label: '\u00c9v\u00e9nement fictif', color: 'rose' },
                  image_manipulation: { icon: '\ud83d\uddbc\ufe0f', label: "Manipulation d'image", color: 'rose' },
                  ai_generated: { icon: '\ud83e\udd16', label: 'Contenu g\u00e9n\u00e9r\u00e9 par IA', color: 'purple' },
                  truncated_quote: { icon: '\u2702\ufe0f', label: 'Citation tronqu\u00e9e/modifi\u00e9e', color: 'amber' },
                  official_contradiction: { icon: '\u274c', label: 'Source officielle contredit', color: 'rose' },
                  satire_parody: { icon: '\ud83d\ude04', label: 'Satire / Parodie', color: 'blue' },
                  wrong_temporal_context: { icon: '\ud83d\udcc5', label: 'Contexte temporel erron\u00e9', color: 'amber' },
                  clickbait: { icon: '\ud83d\udd17', label: 'Clickbait', color: 'amber' },
                  bot_account: { icon: '\ud83e\udd16', label: 'Bot / Compte automatis\u00e9', color: 'purple' },
                };
                const meta = signalMeta[signal.type] || { icon: '\ud83d\udcea', label: signal.type, color: 'slate' };
                const borderColor = signal.severity === 'high' ? 'border-rose-500' : signal.severity === 'medium' ? 'border-amber-500' : 'border-emerald-500';
                const bgColors = { rose: 'bg-rose-500/10', amber: 'bg-amber-500/10', purple: 'bg-purple-500/10', blue: 'bg-blue-500/10', slate: 'bg-white/5' };
                return (
                  <div key={idx} className={`p-3 rounded-lg ${bgColors[meta.color] || 'bg-white/5'} border-l-2 ${borderColor}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{meta.icon}</span>
                      <p className="text-white text-sm font-semibold">{meta.label}</p>
                    </div>
                    <p className="text-slate-300 text-sm ml-7">{signal.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mb-6">
          <h4 className="text-white font-semibold mb-3">{t('result_context')}</h4>
          <div className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap max-h-60 overflow-y-auto">
            {result.context_analysis}
          </div>
        </div>

        {result.limitations && result.limitations.length > 0 && (
          <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <h4 className="text-amber-400 font-semibold mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {t('result_limitations')}
            </h4>
            <ul className="text-slate-300 text-sm space-y-1">
              {result.limitations.map((lim, idx) => (
                <li key={idx}>&bull; {lim}</li>
              ))}
            </ul>
          </div>
        )}

        {result.metadata && result.metadata.credits_used && (
          <div className="mb-6 p-3 rounded-lg bg-white/5 flex items-center justify-between">
            <span className="text-slate-400 text-sm">{t('result_credits_used')}</span>
            <span className="text-white font-medium">{result.metadata.credits_used}</span>
          </div>
        )}

        {(result.verdict === 'probably_fake' || result.verdict === 'uncertain') && (
          <div className="mb-6">
            <button
              onClick={() => setShowChallenge(!showChallenge)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 font-medium hover:bg-amber-500/20 transition-colors"
            >
              <Scale className="w-5 h-5" />
              {showChallenge ? t('result_challenge_cancel') : t('result_challenge')}
            </button>
            {showChallenge && (
              <ChallengeForm
                analysisId={result.id}
                onSubmit={(data) => { onChallenge && onChallenge(data); setShowChallenge(false); }}
                onCancel={() => setShowChallenge(false)}
              />
            )}
          </div>
        )}

        <div className="flex items-center gap-4 pt-4 border-t border-white/10">
          <button
            onClick={copyToClipboard}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-white font-medium"
          >
            {copied ? <CheckCircle className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5" />}
            {copied ? t('result_copied') : t('result_copy')}
          </button>
          <button
            onClick={() => onShare(result)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-blue-600 text-white font-medium hover:opacity-90 transition-opacity"
          >
            <Share2 className="w-5 h-5" />
            {t('result_share')}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
